// Status Codes
const SUCCESS_STATUS = "SUCCESS";
const INCOMPLETE_STATUS_CODE = "SUCCESS202";
const DEFAULT_ERROR = "default";

// Error Codes
const ERROR16_32 = "ERROR16_32";
const ERROR3 = "ERROR3";
const ERROR33 = "ERROR33";
const ERROR13 = "ERROR13";
const ERROR36 = "ERROR36";
const ERROR37 = "ERROR37";
const ERROR29 = "ERROR29"; // promo is not valid
const ERROR30 = "ERROR30"; // user is not eligible for the promo

// Payment Intent Failure Codes
const PAYMENT_INTENT_AUTHENTICATION_FAILURE = "payment_intent_authentication_failure";
const SETUP_INTENT_AUTHENTICATION_FAILURE = "setup_intent_authentication_failure";

// Payment Method Actions
const CREATE_PAYMENT_METHOD = "CREATE-PAYMENT-METHOD";
const UPDATE_PAYMENT_METHOD = "UPDATE-PAYMENT-METHOD";
const FINALIZE_PAYMENT_METHOD = "FINALIZE-PAYMENT-METHOD";

// General Constants
const UNEXPECTED_EXCEPTION = "UNEXPECTED-EXCEPTION";
const CCC_CONTEXT = "CCC";
const CARD_TYPE = "card";
const IF_REQUIRED = "if_required";
const PAYMENT_SUCCESS_URL = "/payment-success";

class PaymentManager {

    // Constructor

    constructor(stripe, vm) {
        this.stripe = stripe;
        this.vm = vm;
    }

    // Core methods

    async processPayment(cardNumber) {
        try {
            this.vm.loadingIndicator(true);
            const beforeCreateSubscriptionResponse = await this.beforeCreateSubscriptionChecks();
            if (beforeCreateSubscriptionResponse.status !== SUCCESS_STATUS) {
                this.handleBackendErrors(beforeCreateSubscriptionResponse, "beforeCreateSubscriptionChecks");
                return;
            }
            let clientSecret = null;
            if (this.vm.freeMonth) {
                const createSetupIntentResponse = await this.createSetupIntent();
                if (!createSetupIntentResponse
                    || createSetupIntentResponse.status !== SUCCESS_STATUS
                    || !createSetupIntentResponse.client_secret) {
                    this.handleBackendErrors(createSetupIntentResponse, "createSetupIntent");
                    return;
                }
                const createPaymentMethodAndConfirmSetupResponse = await this.createPaymentMethodAndConfirmSetup(cardNumber, createSetupIntentResponse.client_secret);
                if (createPaymentMethodAndConfirmSetupResponse.error) {
                    this.handleStripeErrors(createPaymentMethodAndConfirmSetupResponse.error);
                    return
                }

                const paymentMethodId = createPaymentMethodAndConfirmSetupResponse.paymentMethodId;
                const createSubscriptionResponse = await this.createSubscription(paymentMethodId);
                if (createSubscriptionResponse.status !== SUCCESS_STATUS) {
                    this.handleBackendErrors(createSubscriptionResponse, "createSubscription");
                    return;
                }
            } else {
                const { error, paymentMethod } = await this.stripe.createPaymentMethod({
                    type: CARD_TYPE,
                    card: cardNumber,
                    billing_details: {
                        name: this.vm.form.cardHolderFullName,
                        email: this.vm.userEmail,
                    },
                });
                if (error) {
                    this.handleStripeErrors(error);
                    return;
                }
                const subscriptionResponse = await this.createSubscription(paymentMethod.id);
                if (subscriptionResponse.status_code === INCOMPLETE_STATUS_CODE) {
                    clientSecret = subscriptionResponse.client_secret;
                    if (!clientSecret) {
                        this.handleBackendErrors({ status_code: "MISSING_CLIENT_SECRET" }, "createSubscription");
                        return;
                    }
                    let confirmationResult;
                    try {
                        confirmationResult = await this.stripe.confirmPayment({
                            clientSecret,
                            confirmParams: {
                                return_url: window.location.href + PAYMENT_SUCCESS_URL,
                            },
                            redirect: IF_REQUIRED, // Prevent automatic redirection
                        });
                    } catch (error) {
                        this.handleStripeErrors(error);
                    }
                    if (confirmationResult.error) {
                        console.error("Payment confirmation failed:", confirmationResult.error);
                        this.handleStripeErrors(confirmationResult.error);
                        const cancelSubscriptionResponse = await this.cancelSubscription(subscriptionResponse.subscription_id, paymentMethod.id);
                        if (!cancelSubscriptionResponse || cancelSubscriptionResponse.status !== SUCCESS_STATUS) {
                            this.handleBackendErrors(cancelSubscriptionResponse, "cancelSubscription");
                        }
                        return;
                    }
                    const postSubscriptionResponse = await this.postCreateSubscriptionChecks(subscriptionResponse.subscription_id);
                    if (!postSubscriptionResponse || postSubscriptionResponse.status !== SUCCESS_STATUS) {
                        this.handleBackendErrors(postSubscriptionResponse, "postCreateSubscriptionChecks");
                        return;
                    }
                } else if (!subscriptionResponse || subscriptionResponse.status !== SUCCESS_STATUS) {
                    this.handleBackendErrors(subscriptionResponse, "createSubscription");
                    return;
                }
            }
            this.finalizePaymentSuccess();
        } catch (error) {
            console.error("EXCEPTION in payment process:", error);
            this.vm.errorMessage({
                error: {
                    message: error.message || "An unexpected error occurred during the payment process.",
                },
            });
        } finally {
            // Hide loading indicator only at the end of the process
            this.vm.loadingIndicator(false);
        }
    }

    async updateCustomerCC(stripe, card) {
        this.vm.loadingIndicator(true);
        try {
            const result = await stripe.createPaymentMethod({
                type: CARD_TYPE,
                card,
                billing_details: {
                    name: this.vm.form.cardHolderFullName,
                    email: this.vm.userEmail,
                },
            });
            if (result.error) {
                this.handleStripeErrors(result.error);
                this.logError(result.error, CREATE_PAYMENT_METHOD, result.error.message, { context: CCC_CONTEXT, csOrderNo: this.vm.updateCC });
                this.vm.loadingIndicator(false);
                return;
            }
            const updatePaymentMethodResponse = await this.vm.updatePaymentMethod({
                userId: this.vm.userDetails.userId,
                sessionId: this.vm.userDetails.sessionId,
                csOrderNo: this.vm.updateCC,
                paymentMethodId: result.paymentMethod.id,
                subscriber: { name: this.vm.form.cardHolderFullName },
            });
            if (updatePaymentMethodResponse.status !== SUCCESS_STATUS) {
                this.handleBackendErrors(updatePaymentMethodResponse, UPDATE_PAYMENT_METHOD);
                this.vm.loadingIndicator(false);
                return;
            }
            const confirmIntentResult = await this.confirmIntent(
                stripe,
                updatePaymentMethodResponse.intent_type,
                updatePaymentMethodResponse.client_secret
            );
            var intentId = !confirmIntentResult.error ? confirmIntentResult[updatePaymentMethodResponse.intent_type].id : "confirmation_fail";
            const finalConfirmation = await this.vm.finalizeUpdatePaymentMethod({
                userId: this.vm.userDetails.userId,
                sessionId: this.vm.userDetails.sessionId,
                csOrderNo: this.vm.updateCC,
                intentId: intentId,
                intentType: updatePaymentMethodResponse.intent_type,
                paymentMethodId: result.paymentMethod.id,
                subscriber: { name: this.vm.form.cardHolderFullName },
            });
            if (finalConfirmation.status !== SUCCESS_STATUS) {
                this.handleBackendErrors(finalConfirmation, FINALIZE_PAYMENT_METHOD);
                this.vm.loadingIndicator(false);
                return;
            }
            this.finalizePaymentSuccess();
        } catch (error) {
            this.vm.errorMessage(error);
            this.logError(error, UNEXPECTED_EXCEPTION, "", { context: CCC_CONTEXT, csOrderNo: this.vm.updateCC });
        } finally {
            this.vm.loadingIndicator(false);
        }
    }

    // Helper methods and error handling
    async beforeCreateSubscriptionChecks() {
        return this.vm.beforeCreateSubscriptionChecks({
            userId: this.vm.userDetails.userId,
            sessionId: this.vm.userDetails.sessionId,
            csMatNo: this.vm.eowp.item.id,
        });
    }

    async createPaymentMethodAndConfirmSetup(cardNumber, clientSecret) {
        try {
            const { error: createError, paymentMethod } = await this.stripe.createPaymentMethod({
                type: CARD_TYPE,
                card: cardNumber,
                billing_details: {
                    name: this.vm.form.cardHolderFullName,
                    email: this.vm.userEmail,
                },
            });
            if (createError) {
                return { error: createError };
            }
            const confirmationResult = await this.stripe.confirmSetup({
                clientSecret,
                confirmParams: { payment_method: paymentMethod.id },
                redirect: IF_REQUIRED, // Redirect to 3D Secure if needed
            });

            if (confirmationResult.error) {
                console.error("Error trying to confirm setup:", confirmationResult.error);
                return { error: confirmationResult.error };
            }

            if (!confirmationResult.setupIntent || !confirmationResult.setupIntent.payment_method) {
                return { error: "Failed to retrieve payment method from SetupIntent." };
            }
            return { paymentMethodId: confirmationResult.setupIntent.payment_method };
        } catch (err) {
            return { error: err };
        }
    }

    async createSubscription(paymentMethodId) {
        return this.vm.createSubscription({
            userId: this.vm.userDetails.userId,
            sessionId: this.vm.userDetails.sessionId,
            csMatNo: this.vm.eowp.item.id,
            applyFreeMonth: this.vm.freeMonth,
            subscriber: { name: this.vm.form.cardHolderFullName },
            paymentMethodId,
            couponId: this.vm.savedCouponId,
            promoCode: this.vm.savedPromoCode,
        });
    }

    async cancelSubscription(subscriptionId, paymentMethodId) {
        return this.vm.cancelSubscription({
            userId: this.vm.userDetails.userId,
            subscriptionId: subscriptionId,
            paymentMethodId,
            csMatNo: this.vm.eowp.item.id
        });
    }

    async postCreateSubscriptionChecks(subscriptionId) {
        return this.vm.postCreateSubscriptionChecks({
            userId: this.vm.userDetails.userId,
            csMatNo: this.vm.eowp.item.id,
            applyFreeMonth: this.vm.freeMonth,
            subscriptionId: subscriptionId,
        });
    }

    async createSetupIntent() {
        return this.vm.createSetupIntent();
    }

    async confirmIntent(stripe, intentType, clientSecret) {
        const confirmationMethods = {
            paymentIntent: (params) => stripe.confirmPayment(params),
            setupIntent: (params) => stripe.confirmSetup(params)
        };
        if (!confirmationMethods[intentType]) {
            return { error: new Error(`Unknown intent type: ${intentType}`) };
        }
        const commonParams = { clientSecret, redirect: IF_REQUIRED };
        return await confirmationMethods[intentType]({
            ...commonParams
        });
    }

    handleBackendErrors(response, handlerType) {
        const statusCode = response.status_code;
        const defaultErrorMessage = this.vm.form.errors.payment_errors[statusCode]
            ? this.vm.form.errors.payment_errors[statusCode]
            : this.vm.form.errors.payment_errors[DEFAULT_ERROR];
        const errorParams = { complete: true, error: { message: defaultErrorMessage, code: statusCode } };
        switch (statusCode) {
            case ERROR16_32: {
                const errorMessageOverride = this.vm.form.errors.payment_errors[statusCode];
                if (errorMessageOverride) errorParams.error.message = errorMessageOverride;
                this.vm.errorMessage(errorParams);
                break;
            }
            case ERROR3:
            case ERROR33: {
                const productType = this.vm.productTypes[this.vm.eowp.item.id];
                const specificError = this.vm.form.errors.payment_errors[`${statusCode}_${productType}`];
                if (specificError) errorParams.error.message = specificError;
                this.vm.errorMessage(errorParams);
                break;
            }
            case ERROR13: {
                window.location.reload();
                break;
            }
            case ERROR36: {
                const error = {
                    decline_code: PAYMENT_INTENT_AUTHENTICATION_FAILURE,
                };
                this.handleStripeErrors(error);
                break;
            }
            case ERROR37: {
                const error = {
                    decline_code: SETUP_INTENT_AUTHENTICATION_FAILURE,
                };
                this.handleStripeErrors(error);
                break;
            }

            case ERROR29:
            case ERROR30: {
                const errorMessageOverride = this.vm.form.errors.payment_errors[statusCode];
                if (errorMessageOverride) errorParams.error.message = errorMessageOverride;
                this.vm.errorMessage(errorParams);
                break;
            }

            default: {
                const stripeError = this.vm.getStripeError(statusCode);
                if (stripeError) {
                    if (stripeError.message) errorParams.error.message = stripeError.message;
                    if (stripeError.code) errorParams.error.code = stripeError.code;
                }
                this.vm.errorMessage(errorParams);
                break;
            }
        }

        this.logError(response, handlerType, errorParams.error.message);
    }

    handleStripeErrors(error) {
        const { decline_code, code } = error;
        // Use `decline_code` if available, otherwise fallback to `code`
        const errorKey = decline_code || code;
        const customError = this.vm.getStripeError(errorKey);
        // Override error message and code with custom values if they exist for more user-friendly messages
        const errorMessage = customError ? customError.message : this.vm.form.errors.stripe_errors[DEFAULT_ERROR];
        const errorCode = customError ? customError.code : errorKey;
        // Handle errors during SetupIntent confirmation (e.g., 3D Secure failures)
        this.vm.errorMessage({
            complete: true,
            error: {
                message: errorMessage,
                code: errorCode,
            },
        });
    }

    finalizePaymentSuccess() {
        this.vm.setPaymentSuccessFlag('1');
        this.vm.$router.push(PAYMENT_SUCCESS_URL);
    }

    logError(error, handlerType, message = "", extraData = {}) {
        const logMessageParts = [
            `FRONTEND ERROR: (Payment Page > ${extraData.context || "General"}): ERROR-${handlerType.toUpperCase()}, userId: ${this.vm.userDetails.userId}`,
            this.vm.eowp?.item?.id ? `csMatNo: ${this.vm.eowp.item.id}` : null,
            error?.description ? `actualErrorMessage: ${error.description}` : null,
            message ? `frontendErrorMessage: ${message}` : null,
            ...Object.values(extraData)
                .filter(value => value !== undefined && value !== null)
                .map(value => `${value}`),
        ].filter(Boolean).join(", ");

        this.vm.logActivity({ type: "error", message: logMessageParts });
    }

}

export default PaymentManager;